import { createMachine } from "xstate";

import { isManualMeasurementEnabled, isNextGenEnabled } from "../../AppSettings";

import { build_common_questions } from "./v2Questions/CommonQuestionBuilder"

import {
    next_plus_value_route,
    mask_type_route,
} from "./v2Questions/QuestionRouteLogic";

// From https://www.npmjs.com/package/react-browser-detection.  If we need to do more complicated detection we
// can use a package.
//
// This will identify the FireFox Desktop application, and FireFox Android which do not work with the current version
// of MugShot.  The Desktop version does not adhere to video_constraints on the <Video /> element.  On the Android
// version of FireFox a similar problem occurs.
//
// FireFox on iOS is supported.  Apple does not allow web browsers to have their own engine.  All web browsers
// on an iOS device are skinning of the WebKit engine provided by the OS.

const isFirefoxNative = typeof InstallTrigger !== 'undefined';

const doesBrowserSupportMugShot = () => {
    return !isFirefoxNative;
}

export const mugShotNotSupported = () => {
    return !doesBrowserSupportMugShot();
}


export const common_prefixes = ["q_cmm_nsl1_", "q_cmm_nsl2_", "q_cmm_nsl3_", "q_cmm_nsl4_", "q_cmm_full_" ];


const build_next_gen_scan_pages = ( ) => {

    if ( !isNextGenEnabled() ) {
        return {};
    }

    return {
        nextgen_scan_volume: {
            on : {
                NEXT: "nextgen_scan_ears",
            }
        },
        nextgen_scan_help_volume: {
            on: {
                PREVIOUS: "nextgen_scan_volume",
                MANUAL: "sizing_manual_nose_width"
            }
        },
        nextgen_scan_ears: {
            on : {
                NEXT: "nextgen_scan_well_lit",
            }
        },
        nextgen_scan_help_ears: {
            on: {
                PREVIOUS: "nextgen_scan_ears",
                MANUAL: "sizing_manual_nose_width"
            }
        },
        nextgen_scan_well_lit: {
            on : {
                NEXT: "nextgen_scan_level",
            }
        },
        nextgen_scan_help_well_lit: {
            on: {
                PREVIOUS: "nextgen_scan_well_lit",
                MANUAL: "sizing_manual_nose_width"
            }
        },
        nextgen_scan_level: {
            on : {
                NEXT: "nextgen_scan_final_prep",
            }
        },
        nextgen_scan_help_level: {
            on: {
                PREVIOUS: "nextgen_scan_level",
                MANUAL: "sizing_manual_nose_width"
            }
        },
        nextgen_scan_final_prep: {
            on : {
                NEXT: "nextgen_scan_scan_interface",
            }
        },
        nextgen_scan_help_final_prep: {
            on: {
                PREVIOUS: "nextgen_scan_final_prep",
                MANUAL: "sizing_manual_nose_width"
            }
        },
        nextgen_scan_scan_interface: {
            meta: { show_logo: false, headerBgColor: "#001727", headerLinkColor: "#75C7FF"},
            on : {
                NEXT: "nextgen_scan_complete",
                FAILED: "nextgen_scan_scan_retry"
            }
        },
        nextgen_scan_scan_retry: {
            on: {
                NEXT: "nextgen_scan_scan_interface",
                MANUAL: "sizing_manual_nose_width",
            }
        },
        nextgen_scan_complete: {},
    }
}

const build_v2_survey_question_pages = ( before_question_page, questions_done_page, overrides = [] ) => {


    let common_nasal_1 = build_common_questions(common_prefixes[0],
                                                "q_can_breathe_through_nose",
                                                questions_done_page,
                                                overrides);

    let common_nasal_2 = build_common_questions(common_prefixes[1],
                                                "q_nose_breath_exception_reason",
                                                questions_done_page,
                                                overrides);

    let common_nasal_3 = build_common_questions(common_prefixes[2],
                                                "q_can_breathe_through_nose_cl",
                                                questions_done_page,
                                                overrides );

    let common_nasal_4 = build_common_questions(common_prefixes[3],
                                                "q_nose_breath_exception_reason_cl",
                                                questions_done_page,
                                                overrides );

    let common_full = build_common_questions(common_prefixes[4],
                                             "q_mask_type",
                                             questions_done_page,
                                             overrides );

    return {

        q_best_describes_you: {
            meta: { question: "best_describes_you", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: before_question_page,
                NEXT_NEW: "q_breathing_exercise",
                NEXT_CPAP_RETURN: "q_breathing_exercise",
                NEXT_ON_CPAP: "q_refit_reason"
            }
        },

        q_refit_reason: {
          meta: { question: "refit_reason", route_logic: next_plus_value_route },
          on: {
              PREVIOUS: "q_best_describes_you",
              NEXT_BAD_SEAL: "q_mask_type",
              NEXT_UNCOMFORTABLE: "q_mask_type",
              NEXT_IRRITATION: "q_mask_type",
              NEXT_CLAUSTROPHOBIA: "q_mask_type",
              NEXT_AIR_LEAK_MOUTH_BREATH: "q_mask_type",
              NEXT_WONT_STAY_PLACE: "q_mask_type",
              NEXT_WANT_CHANGE: "q_mask_type",
              NEXT_MASK_NOT_AVAILABLE: "q_mask_type"
          }
        },

        q_mask_type: {
            meta: { question: "mask_type", route_logic: mask_type_route },
            on: {
                PREVIOUS: "q_refit_reason",
                NEXT_YES_CLAUSTROPHOBIA: "q_breathing_exercise_cl",
                NEXT_OTHER: `${common_prefixes[4]}gender`
            }
        },

        q_breathing_exercise: {
            meta: { question: "breathing_exercise", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: "q_best_describes_you",
                NEXT: "q_can_breathe_through_nose",
            }
        },

        q_can_breathe_through_nose: {
            meta: { question: "can_breathe_through_nose", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: "q_breathing_exercise",
                NEXT_YES: `${common_prefixes[0]}gender`,
                NEXT_NO: "q_nose_breath_exception_reason",
            }
        },

        q_nose_breath_exception_reason: {
            meta: { question: "nose_breath_exception_reason", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: "q_can_breathe_through_nose",
                NEXT_COLD: `${common_prefixes[1]}gender`,
                NEXT_SINUSITIS: `${common_prefixes[1]}gender`,
                NEXT_ALLERGIES: `${common_prefixes[1]}gender`,
                NEXT_DEV_SEPTUM: `${common_prefixes[1]}gender`,
                NEXT_NASAL_POLYPS: `${common_prefixes[1]}gender`,
                NEXT_OTHER: `${common_prefixes[1]}gender`,
            }
        },

        q_breathing_exercise_cl: {
            meta: { question: "breathing_exercise", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: "q_mask_type",
                NEXT: "q_can_breathe_through_nose_cl",
            }
        },

        q_can_breathe_through_nose_cl: {
            meta: { question: "can_breathe_through_nose", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: "q_breathing_exercise_cl",
                NEXT_YES: `${common_prefixes[2]}gender`,
                NEXT_NO: "q_nose_breath_exception_reason_cl",
            }
        },
        q_nose_breath_exception_reason_cl: {
            meta: { question: "nose_breath_exception_reason", route_logic: next_plus_value_route },
            on: {
                PREVIOUS: "q_can_breathe_through_nose_cl",
                NEXT_COLD: `${common_prefixes[3]}gender`,
                NEXT_SINUSITIS: `${common_prefixes[3]}gender`,
                NEXT_ALLERGIES: `${common_prefixes[3]}gender`,
                NEXT_DEV_SEPTUM: `${common_prefixes[3]}gender`,
                NEXT_NASAL_POLYPS: `${common_prefixes[3]}gender`,
                NEXT_OTHER: `${common_prefixes[3]}gender`,
            }
        },

        // common questions
        ...common_nasal_1,
        ...common_nasal_2,
        ...common_nasal_3,
        ...common_nasal_4,
        ...common_full,


    }
}

// If a page should have a back button then give it a PREVIOUS state.


/**
 *
 * @param currentStep  The state of state machine to start at.
 *
 * @param giveMugShotChoice  Should the choice between face scanning and manual measurement be shown.  If false the
 *                           user will be directed to the manual measurements screen after the questions page.
 *
 * @param skip_questions  Used in test environments.  Should the question pages be included in site navigation.
 *                        Defaults to false.
 *
 * @param overrides An array of strings that represent common questions that should not be displayed.
 *
 * @returns An xstate state machine to be used when navigating the site.
 */
const buildNavMachine = (currentStep,
                         giveMugShotChoice,
                         skip_questions = false,
                         overrides = [] ) => {

    let mugshot_enabled = giveMugShotChoice && doesBrowserSupportMugShot();

    // If we are running a test environment we may be testing face scan only.  In this case,
    // we want to skip the choice page and go directly to the first scan prep page.
    let choice_page = isManualMeasurementEnabled()  ? "sizing_scan_choice" : (
        isNextGenEnabled() ? "nextgen_scan_volume" : "sizing_scan_prep_start"
    )

    // If we've disabled manual measurement for an environment then mugshot_enabled defaults to true.
    if ( !isManualMeasurementEnabled() ) {
        mugshot_enabled = true;
    }

    // If mugshot is enabled then give them a choice page between manual and mugshot measurements
    // otherwise direct them to manual sizing.
    let selection_next_state = mugshot_enabled ? choice_page : "sizing_scan_manual_preamble";

    let v2_survey_question_pages = build_v2_survey_question_pages("patient", selection_next_state, overrides)
    let next_gen_scan_pages = build_next_gen_scan_pages();

    let scan_flow_page = isNextGenEnabled() ? "nextgen_scan_volume" : "sizing_scan_prep_start"

    // Selection is the state where questions are asked. If we are skipping questions, go to the state after
    // the questions.
    let patient_next_state = skip_questions ? selection_next_state : "q_best_describes_you";

    const pageNavigationStates = {
        initial: currentStep,
        predictableActionArguments: true,
        id: "pageNav",
        context: {
            mugshot_enabled: mugshot_enabled
        },
        states: {
            start: {
                on: {NEXT: "patient"}
            },
            patient: {
                on: {NEXT: patient_next_state }
            },
            selection: {
                on: {
                    NEXT: selection_next_state,
                    PREVIOUS: "patient"
                }
            },

            ...v2_survey_question_pages,
            ...next_gen_scan_pages,

            sizing_scan_manual_preamble: {
                on: {
                    NEXT: "sizing_manual_nose_width",
                }
            },
            sizing_manual_nose_width: {
                on: {
                    NEXT: "sizing_manual_face_height",
                    PREVIOUS: "sizing_scan_manual_preamble",
                }
            },
            sizing_manual_face_height: {
                on : {
                    NEXT: "sizing_manual_nose_depth",
                    PREVIOUS: "sizing_manual_nose_width"
                }
            },
            sizing_manual_nose_depth: {
                on : {
                    NEXT: "sizing_manual_complete",
                    PREVIOUS: "sizing_manual_face_height"
                }
            },
            sizing_manual_complete: {
                on : {
                    NEXT: "complete",
                    PREVIOUS: "sizing_manual_nose_depth",
                    TRY_AGAIN: "sizing_manual_complete",
                    ERROR: "error"
                }
            },
            sizing_manual_nose_width_prep: {
                on: {
                    NEXT: "sizing_manual_face_height_prep",
                    PREVIOUS: "sizing_scan_prep",
                }
            },
            sizing_manual_face_height_prep: {
                on : {
                    NEXT: "sizing_manual_nose_depth_prep",
                    PREVIOUS: "sizing_manual_nose_width_prep"
                }
            },
            sizing_manual_nose_depth_prep: {
                on : {
                    NEXT: "sizing_manual_complete_prep",
                    PREVIOUS: "sizing_manual_face_height_prep"
                }
            },
            sizing_manual_complete_prep: {
                on : {
                    NEXT: "complete",
                    PREVIOUS: "sizing_manual_nose_depth_prep",
                    TRY_AGAIN: "sizing_manual_complete_prep",
                    ERROR: "error"
                }
            },
            sizing_scan_choice: {
                on : {
                    MANUAL: "sizing_scan_manual_preamble",
                    NEXT: scan_flow_page,
                }
            },
            sizing_scan_prep_start: {
              on : {
                  PREVIOUS: "sizing_scan_choice",
                  NEXT: "sizing_scan_prep_well_lit",
                  HELP: "sizing_scan_help_well_lit"
              }
            },
            sizing_scan_prep_well_lit: {
                on: {
                    PREVIOUS: "sizing_scan_choice",
                    NEXT: "sizing_scan_prep_centered",
                    HELP: "sizing_scan_help_well_lit"
                }
            },
            sizing_scan_prep_mouth_closed: {
                on: {
                    PREVIOUS: "sizing_scan_prep_well_lit",
                    NEXT: "sizing_scan_prep_centered",
                    HELP: "sizing_scan_help_mouth_closed"
                }
            },
            sizing_scan_prep_centered: {
                on: {
                    PREVIOUS: "sizing_scan_prep_mouth_closed",
                    NEXT: "sizing_scan_prep_level",
                    HELP: "sizing_scan_help_centered"
                }
            },
            sizing_scan_prep_level: {
                on: {
                    PREVIOUS: "sizing_scan_prep_centered",
                    NEXT: "sizing_scan_prep",
                    HELP: "sizing_scan_help_level"
                }
            },
            sizing_scan_prep: {
                on : {
                    PREVIOUS: "sizing_scan_choice",
                    NEXT: "sizing_scan_prep_glasses",
                    HELP: "sizing_scan_help_prep"
                }
            },
            sizing_scan_prep_glasses: {
                on: {
                    PREVIOUS: "sizing_scan_prep",
                    NEXT: "sizing_scan_take_photo",
                    HELP: "sizing_scan_help_glasses"
                }
            },
            sizing_scan_take_photo: {
                on: {
                    PREVIOUS: "sizing_scan_prep",
                    FAILED: "sizing_scan_retry",
                    NEXT: "sizing_scan_take_photo_analyzing",
                    NO_CAMERA_MANUAL: "sizing_manual_nose_width_no_camera",
                    HELP: "sizing_scan_help_take_photo"
                }
            },
            sizing_scan_help_prep: {
                on: {
                    PREVIOUS: "sizing_scan_prep",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_help_take_photo: {
                on: {
                    PREVIOUS: "sizing_scan_take_photo",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_help_well_lit: {
                on: {
                    PREVIOUS: "sizing_scan_prep_well_lit",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_help_mouth_closed: {
                on: {
                    PREVIOUS: "sizing_scan_prep_mouth_closed",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_help_glasses: {
                on: {
                    PREVIOUS: "sizing_scan_prep_glasses",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_help_centered: {
                on: {
                    PREVIOUS: "sizing_scan_prep_centered",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_help_level: {
                on: {
                    PREVIOUS: "sizing_scan_prep_level",
                    MANUAL: "sizing_manual_nose_width"
                }
            },
            sizing_scan_retry: {
              on: {
                  NEXT: "sizing_scan_take_photo",
                  MANUAL: "sizing_manual_nose_width",
                  HELP: "sizing_scan_help_take_photo"
              }
            },
            sizing_scan_take_photo_analyzing: {
                on: {
                    NEXT: "complete",
                    FAILED: "sizing_scan_retry"
                }
            },
            sizing_manual_nose_width_no_camera: {
                on: {
                    NEXT: "sizing_manual_face_height_no_camera",
                    PREVIOUS: "sizing_scan_take_photo",
                }
            },
            sizing_manual_face_height_no_camera: {
                on : {
                    NEXT: "sizing_manual_nose_depth_no_camera",
                    PREVIOUS: "sizing_manual_nose_width_no_camera"
                }
            },
            sizing_manual_nose_depth_no_camera: {
                on : {
                    NEXT: "sizing_manual_complete",
                    PREVIOUS: "sizing_manual_face_height_no_camera"
                }
            },
            sizing_manual_complete_no_camera: {
                on : {
                    NEXT: "complete",
                    PREVIOUS: "sizing_manual_nose_depth_no_camera",
                    TRY_AGAIN: "sizing_manual_complete_no_camera",
                    ERROR: "error"
                }
            },
            complete: {},
            error: {},
        }
    }

    return createMachine(pageNavigationStates);
}

const stateHasPrevious = (state) => {
    return state.nextEvents.indexOf("PREVIOUS") >= 0;
}

const stateHasHelp = (state) => {
    return state.nextEvents.indexOf("HELP") >= 0;
}

export { buildNavMachine, stateHasPrevious, stateHasHelp }